<script setup>
import { drawMeme } from '@bignerve/meme-generator';
import { Buffer } from 'buffer';
import axios from 'axios';
import { ref, watch, nextTick, onMounted, useTemplateRef } from 'vue';
import { api } from '/app/plugins/services';
import { sentry } from '/app/plugins/sentry';
import { config } from '/app/config';
import NLoading from '/app/components/NLoading.vue';

const loadImage = (source) => {
    const img = new Image();
    img.src = source;
    return new Promise((resolve, reject) => {
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Could not load image at ' + source));
    });
};

const useBaseDimensions = (img) => ({
    width: 500,
    height: (img.height / img.width) * 500,
});

const props = defineProps({
    // The image to be displayed as context
    imageSrc: String,
    // The setup text (title)
    setupText: String,
    // The pushline text (description)
    punchlineText: String,
    // format (meme|caption)
    format: {
        type: String,
        default: 'meme',
    },
    // shows loading indicator
    loading: {
        type: Boolean,
        default: false,
    },
});

const canvas = useTemplateRef('canvas');
const busy = ref(false);
const logo = ref(null);
const width = ref(500);
const height = ref(500);

async function draw() {
    try {
        const img = await loadImage(props.imageSrc);
        if (canvas.value) {
            renderMeme(canvas.value, img);
        }
    } catch (error) {
        console.warn('Failed to load meme image', error);
    }
}

function renderMeme(canvasEl, image) {
    const dims = useBaseDimensions(image);

    width.value = dims.width;
    height.value = dims.height;

    const ctx = canvasEl.getContext('2d');
    ctx.clearRect(0, 0, width.value, height.value);

    drawMeme(canvasEl, {
        setupText: props.setupText,
        punchlineText: props.punchlineText,
        image,
        format: props.format,
        height: height.value,
        width: width.value,
        logo: logo.value,
        footer: true,
    });

    return canvasEl;
}

async function loadLogo() {
    try {
        logo.value = await loadImage(`${location.origin}/img/brain.svg`);
    } catch (error) {
        console.warn('Failed to load logo', error);
    }
}

function toFile(canvasEl) {
    return new Promise((resolve, reject) => {
        try {
            canvasEl.toBlob((blob) => {
                resolve(new File([blob], 'image.png', { type: 'image/png' }));
            }, 'image/png');
        } catch (e) {
            reject(e);
        }
    });
}

async function uploadImage() {
    try {
        busy.value = true;
        const { data, headers } = await axios.get(
            `${config.functions.baseURL}/use-image?source=${props.imageSrc}`,
            { responseType: 'arraybuffer' },
        );
        const base64 = Buffer.from(data).toString('base64');
        const imageBase64 = `data:${headers['Content-Type']};base64,${base64}`;

        const image = await loadImage(imageBase64);
        const offscreen = document.createElement('canvas');

        renderMeme(offscreen, image);

        const file = await toFile(offscreen);
        const { data: response } = await api.resource.uploadImage(
            file, { type: file.type }, { 'Content-Type': file.type },
        );

        return response;
    } catch (err) {
        console.log(err);
        sentry.withScope(scope => {
            scope.setTag('event', 'meme-generator');
            scope.setTag('step', 'upload-image');

            sentry.captureException(err);
        });
    } finally {
        busy.value = false;
    }
}

function init() {
    loadLogo();
    draw();
}

watch(() => props.setupText, draw);
watch(() => props.punchlineText, draw);
watch(() => props.imageSrc, draw);

onMounted(() => {
    nextTick(init);
});

defineExpose({ uploadImage });
</script>

<template>
    <div class="relative">
        <div v-if="busy && loading" class="bg-black bg-opacity-40 absolute inset-0 flex items-center justify-center">
            <NLoading class="text-white" active>
                Uploading image...
            </NLoading>
        </div>
        <canvas ref="canvas" class="w-full"></canvas>
    </div>
</template>
